import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FC, memo } from "react";
import type { ChronologyEvent } from "~/api/model";
import type { ChronologyDuplicateRowInfo } from "./ChronologyDuplicateDot";
import { ChronologyEventItem } from "./ChronologyEventItem";

interface SortableChronologyItemProps {
  event: ChronologyEvent;
  duplicateInfo?: ChronologyDuplicateRowInfo;
  onReviewDuplicates: (eventIds: string[]) => void;
}

const SortableChronologyItemInner: FC<SortableChronologyItemProps> = ({
  event,
  duplicateInfo,
  onReviewDuplicates,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: event.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <ChronologyEventItem
        event={event}
        dragHandleListeners={listeners}
        dragHandleAttributes={attributes}
        isDragging={isDragging}
        duplicateInfo={duplicateInfo}
        onReviewDuplicates={onReviewDuplicates}
      />
    </div>
  );
};

export const SortableChronologyItem = memo(
  SortableChronologyItemInner,
  (prev, next) => {
    if (prev.event !== next.event) return false;
    if (prev.onReviewDuplicates !== next.onReviewDuplicates) return false;
    const prevInfo = prev.duplicateInfo;
    const nextInfo = next.duplicateInfo;
    if (prevInfo === nextInfo) return true;
    if (!prevInfo || !nextInfo) return false;
    if (prevInfo.colorIndex !== nextInfo.colorIndex) return false;
    const prevIds = prevInfo.eventIds;
    const nextIds = nextInfo.eventIds;
    if (prevIds.length !== nextIds.length) return false;
    return prevIds.every((id, i) => id === nextIds[i]);
  }
);
